import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  getHemisphere,
  setHemisphere as persistHemisphere,
  seasonalMonth,
} from '@/lib/hemisphere';
import type { Hemisphere } from '@/lib/hemisphere';

interface HemisphereContextValue {
  hemisphere: Hemisphere;
  setHemisphere: (h: Hemisphere) => void;
  isSouthern: boolean;
  /** The northern-hemisphere data month matching `month` in this hemisphere. */
  dataMonth: (month: number) => number;
}

const HemisphereContext = createContext<HemisphereContextValue | null>(null);

/**
 * Site-wide northern / southern hemisphere preference.
 *
 * The plant database records sowing and harvest months for a northern grower,
 * so every page that reads plant.calendar against a displayed month has to map
 * through `dataMonth` first. Reading the preference through context rather than
 * calling getHemisphere() directly means switching updates the whole site at
 * once instead of only on the next full page load.
 */
export function HemisphereProvider({ children }: { children: React.ReactNode }) {
  const [hemisphere, setState] = useState<Hemisphere>(() => getHemisphere());

  const setHemisphere = useCallback((h: Hemisphere) => {
    persistHemisphere(h);
    setState(h);
  }, []);

  const value = useMemo<HemisphereContextValue>(
    () => ({
      hemisphere,
      setHemisphere,
      isSouthern: hemisphere === 'southern',
      dataMonth: (month: number) => seasonalMonth(month, hemisphere),
    }),
    [hemisphere, setHemisphere],
  );

  return <HemisphereContext.Provider value={value}>{children}</HemisphereContext.Provider>;
}

export function useHemisphere(): HemisphereContextValue {
  const ctx = useContext(HemisphereContext);
  if (!ctx) throw new Error('useHemisphere must be used within a HemisphereProvider');
  return ctx;
}
